import { useQuery } from '@tanstack/react-query';
import { Loader2, PackageCheck, RefreshCw } from 'lucide-react';
import type { NsdcKit } from '@tracker/shared';
import { trackerFdfcService } from '@/services/tracker-fdfc.service';
import { getErrorMessage } from '@/lib/errors';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

function kitPaymentLabel(kit: Pick<NsdcKit, 'paymentType' | 'amount'>): string {
  if (kit.paymentType === 'cod') return kit.amount === null ? 'COD' : `COD ₹${kit.amount.toLocaleString('en-IN')}`;
  return kit.paymentType === 'prepaid' ? 'Prepaid' : 'Free';
}

function kitBoxLabel(kit: NsdcKit): string | null {
  const b = kit.box;
  if (!b) return null;
  const parts: string[] = [];
  if (b.weightGrams) parts.push(`${b.weightGrams} g`);
  if (b.lengthCm && b.breadthCm && b.heightCm) parts.push(`${b.lengthCm}×${b.breadthCm}×${b.heightCm} cm`);
  return parts.length ? parts.join(' · ') : null;
}

/**
 * The NSDC kit to send: the kits NSDC is accepting orders for (loaded by our
 * backend), a picker, and what the chosen kit will ship. Shared by the bulk
 * Dispatch dialog and "Dispatch another order". The server re-checks the kit
 * live when sending, so a kit closed in the meantime is refused there.
 */
export function KitPicker({
  enabled,
  kitCode,
  onChange,
  label = 'NSDC kit order',
}: {
  /** Load the kit list only while the dialog is open. */
  enabled: boolean;
  kitCode: string;
  onChange: (kitCode: string) => void;
  label?: string;
}) {
  const kitsQuery = useQuery({
    queryKey: ['tracker-fdfc', 'nsdc-kits'],
    queryFn: () => trackerFdfcService.nsdcKits(),
    enabled,
  });
  const kits = kitsQuery.data?.kits ?? [];
  const kit = kits.find((k) => k.kitCode === kitCode) ?? null;

  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium">{label}</label>
      {kitsQuery.isLoading ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading kits from NSDC…
        </p>
      ) : kitsQuery.data && !kitsQuery.data.configured ? (
        <p className="rounded-lg border border-amber-300/60 bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
          NSDC is not connected on this server. Set NSDC_API_BASE_URL and NSDC_API_KEY in the backend environment.
        </p>
      ) : kitsQuery.isError || kitsQuery.data?.error ? (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-rose-300/60 bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">
          <span>{kitsQuery.data?.error ?? getErrorMessage(kitsQuery.error)}</span>
          <Button size="sm" variant="outline" onClick={() => void kitsQuery.refetch()}>
            <RefreshCw className="h-4 w-4" /> Retry
          </Button>
        </div>
      ) : kits.length === 0 ? (
        <p className="text-sm text-muted-foreground">NSDC has no kit order open for orders right now.</p>
      ) : (
        <Select value={kitCode} onValueChange={onChange}>
          <SelectTrigger className="h-auto min-h-9 w-full py-2 text-left">
            <SelectValue placeholder="Choose the kit to send" />
          </SelectTrigger>
          <SelectContent>
            {kits.map((k) => (
              <SelectItem key={k.kitCode} value={k.kitCode}>
                {k.title} · {k.kitCode} · {kitPaymentLabel(k)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {kit && (
        <div className="rounded-lg border border-border/60 bg-muted/30 p-3 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <PackageCheck className="h-4 w-4 text-primary" />
            <span className="font-medium">{kit.title}</span>
            <Badge variant="secondary">{kitPaymentLabel(kit)}</Badge>
            {kitBoxLabel(kit) && <span className="text-xs text-muted-foreground">{kitBoxLabel(kit)}</span>}
          </div>
          {kit.batchTitle && <p className="mt-1 text-xs text-muted-foreground">Batch: {kit.batchTitle}</p>}
          {kit.contents.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-xs text-muted-foreground">
              {kit.contents.map((c, i) => (
                <li key={i}>
                  {c.quantity} × {c.title}
                  {c.sku && ` · ${c.sku}`}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
